import React from 'react';

import { useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import LegacyButton from '@atlaskit/button/standard-button';
import CloseIcon from '@atlaskit/icon/core/migration/close--editor-close';
import CopyIcon from '@atlaskit/icon/core/migration/copy';
import Tooltip from '@atlaskit/tooltip';

import { type Variant } from '../../types';
import { DayTimeRangePicker } from '../day-time-range-picker';
import { type DayTimeRangePickerValue } from '../day-time-range-picker/types';
import { FullWidthFlex } from '../styled';

import messages from './messages';
import { ActionsWrapper } from './styled';
import { type DayTimeIntervalProps } from './types';

const initialValue: DayTimeRangePickerValue = {
	startDay: null,
	startHour: null,
	startMinute: null,
	endDay: null,
	endHour: null,
	endMinute: null,
};

const rowColumnSpacing: Record<Variant, number> = {
	default: 1.5,
	compact: 1,
};

const actionsColumnSpacing: Record<Variant, number> = {
	default: 1,
	compact: 0.5,
};

export const DayTimeInterval: React.FC<DayTimeIntervalProps> = (props) => {
	const {
		value,
		labels,
		weekStartDay,
		onChange,
		newIntervalValue,
		isDisabled,
		timeIsEditable,
		autoAddNextDay,
	} = props;

	const variant = props.variant ?? 'default';

	const { formatMessage } = useIntl();

	const handleDelete = (deletedItemIndex: number) => {
		const newIntervals = value?.filter((_, i) => i !== deletedItemIndex);

		if (onChange) {
			onChange(newIntervals);
		}
	};

	const handleAdd = () => {
		let newInterval = newIntervalValue || initialValue;

		if (!newIntervalValue && autoAddNextDay && value?.length > 0) {
			// copy and create new interval, otherwise it will be the same object and will change the original value
			const lastInterval = { ...value[value.length - 1] };
			const { startDay, endDay } = lastInterval;

			// if last interval doesn't have a value, don't do anything
			if (startDay && endDay) {
				lastInterval.startDay = startDay === 7 ? 7 : startDay + 1;
				lastInterval.endDay = endDay === 7 ? 7 : endDay + 1;

				newInterval = lastInterval;
			}
		}

		const newIntervals = [...value, { ...newInterval, key: new Date().getTime(), isNew: true }];

		onChange(newIntervals);
	};

	const handleClone = (interval: DayTimeRangePickerValue, clonedItemIndex: number) => {
		const newIntervals = [...value];

		const newInterval = { ...interval, key: new Date().getTime() };
		Array.prototype.splice.call(newIntervals, clonedItemIndex + 1, 0, newInterval);

		onChange(newIntervals);
	};

	const handleChange = (values: DayTimeRangePickerValue, changedItemIndex: number) => {
		const newIntervals = value?.map((interval, i) => {
			if (i === changedItemIndex) {
				return { ...values, ...(interval.key ? { key: interval.key } : {}) };
			}

			return interval;
		});

		onChange(newIntervals);
	};

	return (
		<FullWidthFlex flexDirection="column" rowGap={1}>
			{value.map((interval, i) => (
				<FullWidthFlex
					key={interval.key || i}
					columnGap={rowColumnSpacing[variant]}
					alignItems="flex-end"
				>
					<DayTimeRangePicker
						weekStartDay={weekStartDay}
						labels={labels}
						value={interval}
						onChange={(values) => handleChange(values, i)}
						isDisabled={isDisabled}
						timeIsEditable={timeIsEditable}
						timeSlotIndex={i}
						variant={variant}
					/>
					<ActionsWrapper variant={variant} columnGap={actionsColumnSpacing[variant]}>
						<Tooltip content={formatMessage(messages.clone)}>
							<IconButton
								label={formatMessage(messages.clone)}
								spacing="compact"
								appearance="subtle"
								onClick={() => handleClone(interval, i)}
								isDisabled={isDisabled}
								icon={(iconProps) => <CopyIcon {...iconProps} LEGACY_size="small" />}
							/>
						</Tooltip>
						<Tooltip content={formatMessage(messages.delete)}>
							<IconButton
								label={formatMessage(messages.delete)}
								spacing="compact"
								appearance="subtle"
								icon={(iconProps) => <CloseIcon {...iconProps} LEGACY_size="small" />}
								onClick={() => handleDelete(i)}
								isDisabled={isDisabled ? isDisabled : value.length < 2}
							/>
						</Tooltip>
					</ActionsWrapper>
				</FullWidthFlex>
			))}
			<div>
				<LegacyButton
					isDisabled={isDisabled}
					onClick={handleAdd}
					spacing="none"
					// TODO: (from codemod) "link" and "subtle-link" appearances are only available in LinkButton, please either provide a href prop then migrate to LinkButton, or remove the appearance from the default button.
					// https://product-fabric.atlassian.net/browse/DSP-19027
					appearance="link"
				>
					{formatMessage(messages.addNew)}
				</LegacyButton>
			</div>
		</FullWidthFlex>
	);
};
